import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Form, Formik, useFormikContext } from 'formik';

import { getAllSearchableContactRoles } from '~/components/communications/ContactUtils';
import ContactTextFieldFormik from '~/components/ContactTextFieldFormik';
import Button from '~/components/core/Atomic/Buttons/Button';
import MenuItem from '~/components/core/Atomic/MenuItem';
import Heading from '~/components/core/TextComponents/Heading';
import Text from '~/components/core/TextComponents/Text';
import useOrganization from '~/components/OrganizationContext';
import TextFieldFormik, {
  DatePickerTextFieldFormik,
  MonetaryValueTextFieldFormik,
  NumericTextFieldFormik,
} from '~/components/TextFieldFormik';
import type { ClaimPolymorph } from '~/components/types/claim-types';
import type { Exposure } from '~/server_shared/swagger/data-contracts';
import { reportAxiosError, reportErrorInProductionOrThrow } from '~/Utils';

import { createIncomeProtectionDaSetting, updateIncomeProtectionDaSetting } from '../apiService';
import { INCOME_PROTECTION_SETTING_CREATION_KEYS_MAPPED, REQUIRED_INCOME_PROTECTION_SETTING_KEYS } from '../consts';
import TEXTS from '../texts';
import type {
  IncomeProtectionDaSettingCreationType,
  IncomeProtectionDaSettingType,
  SettingsFormInitialValuesType,
} from '../types';

import IncomeProtectionBenefitPeriodRow from './IncomeProtectionBenefitPeriodRow';
import {
  CUSTOM_PAYMENT_FREQUENCY_VALUE,
  getInitialValues,
  PAYMENT_FREQUENCY_OPTIONS,
  useHandleChangeAndComputeClaimantWeeklyLimit,
  validationSchema,
} from './incomeProtectionSettingsFormHelpers';

const DAY_MS = 24 * 60 * 60 * 1000;

type IncomeProtectionSettingsFormProps = {
  onCancel: () => void;
  setting?: IncomeProtectionDaSettingType;
  onSuccess?: () => Promise<unknown> | unknown;
  exposure: Exposure & { id: number };
  claim: ClaimPolymorph;
};

const checkIsIncomeProtectionDaSettingType = (
  values: Partial<SettingsFormInitialValuesType>
): values is IncomeProtectionDaSettingCreationType => {
  for (const key of REQUIRED_INCOME_PROTECTION_SETTING_KEYS) {
    if (values[key] === null || values[key] === undefined) {
      return false;
    }
  }
  return true;
};

const CUSTOM_PAYMENT_FREQUENCY_STATES = {
  SHOWN: 'SHOWN',
  SHOWN_AUTOFOCUS: 'SHOWN_AUTOFOCUS',
  HIDDEN: 'HIDDEN',
} as const;

const IncomeProtectionSettingsFormInner = ({
  onCancel,
  claimDateOfLossISOString,
}: {
  onCancel: () => void;
  claimDateOfLossISOString: string | null;
}) => {
  const { setFieldValue, handleChange, setFieldTouched, isSubmitting, initialValues } =
    useFormikContext<SettingsFormInitialValuesType>();
  const [customPaymentFrequencyState, setCustomPaymentFrequencyState] = useState<
    keyof typeof CUSTOM_PAYMENT_FREQUENCY_STATES
  >(
    initialValues.payment_frequency_internal === CUSTOM_PAYMENT_FREQUENCY_VALUE
      ? CUSTOM_PAYMENT_FREQUENCY_STATES.SHOWN
      : CUSTOM_PAYMENT_FREQUENCY_STATES.HIDDEN
  );
  const paymentFrequencyInputRef = useRef<HTMLInputElement | null>(null);
  // @ts-expect-error - useOrganization is not typed
  const { organizationContactRolesDict } = useOrganization();

  const handleWaitingPeriodChangeAndUpdateStartDate = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleChange(event);

      if (claimDateOfLossISOString) {
        const value = Number(event.target.value);
        if (!isNaN(value) && value >= 1) {
          // Parse as UTC to avoid local timezone from shifting the db date
          const claimDateOfLossUTC = new Date(`${claimDateOfLossISOString}T12:00:00.000Z`);
          const startDateUTC = new Date(Number(claimDateOfLossUTC) + value * DAY_MS);
          const startDateISO = startDateUTC.toISOString().split('T')[0];
          setFieldValue(INCOME_PROTECTION_SETTING_CREATION_KEYS_MAPPED.start_date, startDateISO);
        }
      }
    },
    [handleChange, claimDateOfLossISOString, setFieldValue]
  );

  const handleInternalPaymentFrequencyChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      handleChange(event);
      setCustomPaymentFrequencyState(
        value === CUSTOM_PAYMENT_FREQUENCY_VALUE
          ? CUSTOM_PAYMENT_FREQUENCY_STATES.SHOWN_AUTOFOCUS
          : CUSTOM_PAYMENT_FREQUENCY_STATES.HIDDEN
      );
      setFieldValue(
        INCOME_PROTECTION_SETTING_CREATION_KEYS_MAPPED.payment_frequency_num_of_days,
        value === CUSTOM_PAYMENT_FREQUENCY_VALUE ? '' : value
      );
      if (value === CUSTOM_PAYMENT_FREQUENCY_VALUE) {
        setFieldTouched(INCOME_PROTECTION_SETTING_CREATION_KEYS_MAPPED.payment_frequency_num_of_days, false);
      }
    },
    [handleChange, setFieldValue, setFieldTouched]
  );

  const handleChangeAndComputeClaimantWeeklyLimit = useHandleChangeAndComputeClaimantWeeklyLimit();

  useEffect(() => {
    if (customPaymentFrequencyState === CUSTOM_PAYMENT_FREQUENCY_STATES.SHOWN_AUTOFOCUS) {
      paymentFrequencyInputRef.current?.focus();
    }
  }, [customPaymentFrequencyState]);

  return (
    <Form className="grid gap-40">
      <div className="grid grid-cols-2 gap-16 pt-12">
        <Text className="col-span-2" variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
          {TEXTS.eligibilityAndTiming}
        </Text>
        <IncomeProtectionBenefitPeriodRow />
        <TextFieldFormik
          id={INCOME_PROTECTION_SETTING_CREATION_KEYS_MAPPED.waiting_period_num_of_days}
          label={TEXTS.settingFormLabels.waiting_period_num_of_days}
          InputProps={{ inputProps: { type: 'number', inputMode: 'numeric', min: 0 } }}
          // @ts-expect-error - TextFieldFormik is not yet typed
          onChange={handleWaitingPeriodChangeAndUpdateStartDate}
        />
        <DatePickerTextFieldFormik
          id={INCOME_PROTECTION_SETTING_CREATION_KEYS_MAPPED.start_date}
          label={TEXTS.settingFormLabels.start_date}
          className="w-full"
          clearable
        />
        <TextFieldFormik
          id="payment_frequency_internal"
          select
          label={TEXTS.settingFormLabels.payment_frequency_num_of_days}
          // @ts-expect-error - TextFieldFormik is not yet typed
          onChange={handleInternalPaymentFrequencyChange}
        >
          {/* @ts-expect-error - TextFieldFormik is not yet typed */}
          {PAYMENT_FREQUENCY_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextFieldFormik>
        {customPaymentFrequencyState !== CUSTOM_PAYMENT_FREQUENCY_STATES.HIDDEN ? (
          <TextFieldFormik
            id={INCOME_PROTECTION_SETTING_CREATION_KEYS_MAPPED.payment_frequency_num_of_days}
            label={TEXTS.settingFormLabels.payment_frequency_custom}
            InputProps={{ inputProps: { type: 'number', inputMode: 'numeric', min: 1 } }}
            // @ts-expect-error - TextFieldFormik is not yet typed
            inputWrapperRef={(el: HTMLDivElement | null) => {
              paymentFrequencyInputRef.current = el?.querySelector('input') || null;
            }}
          />
        ) : null}
      </div>
      <div className="grid grid-cols-3 gap-16">
        <Text className="col-span-3" variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
          {TEXTS.income}
        </Text>
        <MonetaryValueTextFieldFormik
          id={INCOME_PROTECTION_SETTING_CREATION_KEYS_MAPPED.average_weekly_income}
          label={TEXTS.settingFormLabels.average_weekly_income}
          onChange={(value) =>
            handleChangeAndComputeClaimantWeeklyLimit(
              INCOME_PROTECTION_SETTING_CREATION_KEYS_MAPPED.average_weekly_income,
              value
            )
          }
        />
        <NumericTextFieldFormik
          id={INCOME_PROTECTION_SETTING_CREATION_KEYS_MAPPED.weekly_income_percentage}
          label={TEXTS.settingFormLabels.weekly_income_percentage}
          suffix="%"
          isAllowed={({ floatValue }) => floatValue === undefined || (floatValue >= 0 && floatValue <= 100)}
          onChange={(value) =>
            handleChangeAndComputeClaimantWeeklyLimit(
              INCOME_PROTECTION_SETTING_CREATION_KEYS_MAPPED.weekly_income_percentage,
              value
            )
          }
        />
        <MonetaryValueTextFieldFormik
          id={INCOME_PROTECTION_SETTING_CREATION_KEYS_MAPPED.weekly_payment_limit}
          label={TEXTS.settingFormLabels.weekly_payment_limit}
          onChange={(value) =>
            handleChangeAndComputeClaimantWeeklyLimit(
              INCOME_PROTECTION_SETTING_CREATION_KEYS_MAPPED.weekly_payment_limit,
              value
            )
          }
        />
        <MonetaryValueTextFieldFormik
          id={INCOME_PROTECTION_SETTING_CREATION_KEYS_MAPPED.claimant_weekly_limit}
          label={TEXTS.settingFormLabels.claimant_weekly_limit}
        />
      </div>
      <div className="grid grid-cols-2 gap-16">
        <ContactTextFieldFormik
          id="payee_contact"
          label={TEXTS.settingFormLabels.payee_contact_id}
          acceptedRoles={getAllSearchableContactRoles(organizationContactRolesDict)}
          fixedSearchResults
        />
      </div>
      <div className="flex justify-end gap-16">
        <Button type="button" variant="outlined" color="primary" onClick={onCancel} disabled={isSubmitting}>
          {TEXTS.cancel}
        </Button>
        <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
          {TEXTS.submitSettings}
        </Button>
      </div>
    </Form>
  );
};

const IncomeProtectionSettingsForm = ({
  onCancel,
  onSuccess,
  exposure,
  claim,
  setting,
}: IncomeProtectionSettingsFormProps) => {
  const exposureId = exposure.id;
  const isCreate = !setting;
  const handler = isCreate ? createIncomeProtectionDaSetting : updateIncomeProtectionDaSetting;

  return (
    <Formik
      initialValues={getInitialValues(exposureId, setting)}
      validationSchema={validationSchema}
      onSubmit={async (values) => {
        const {
          payment_frequency_internal,
          payee_contact,
          payee_contact_full_name,
          benefit_period_internal,
          ...requiredValues
        } = values;
        if (!checkIsIncomeProtectionDaSettingType(requiredValues)) {
          // (For typescript's sake - shouldn't actually happen)
          reportErrorInProductionOrThrow('Invalid income protection DA setting values');
          return;
        }
        try {
          await handler({
            organizationId: claim.organization_id,
            claimId: claim.id,
            exposureId,
            data: requiredValues,
          });
          if (onSuccess) {
            await onSuccess();
          }
        } catch (error) {
          reportAxiosError(error);
        }
      }}
    >
      <IncomeProtectionSettingsFormInner
        onCancel={onCancel}
        claimDateOfLossISOString={claim.incident?.date_of_loss || null}
      />
    </Formik>
  );
};

IncomeProtectionSettingsForm.RECOMMENDED_CARD_DIALOG_VIEW_PROPS = {
  fullWidth: true,
  classes: { paper: 'min-w-[640px]' },
  title: <Heading variant={Heading.TYPES.H1}>{TEXTS.settingsDialogTitle}</Heading>,
} as const;

export default IncomeProtectionSettingsForm;
